import React, { useContext, useState } from 'react';
import { Title, UrlContainer, UrlContent, UrlDisplay, UrlEdit, EditIcon, EditInput, EditContent, CharacterLeft, Actions, Error, EditButtons, EditButton, ButtonContainer, SocialListContainer, SocialIcon, Button } from "./ShareSocials.style";
import { copyToClipBoard } from 'components/utils';
import CMSContext from 'context/CMSContext';

const enum SOCIAL_NETWORK_TYPE {
    copyUrl = 'copyUrl',
    whatsApp = 'whatsapp',
    facebook = 'facebook',
    twitter = 'twitter',
    linkedIn = 'linkedin',
    email = 'email'
}

export const enum SHARE_LOCATION {
    story = 'story',
    donate = 'donate',
    events = 'events',
    mospace = 'mospace',
    general = 'general'
}

interface Props {
    closeModalFn?: () => void, //Optional, if displayed in a modal this will be supplied by modal.tsx

    shareUrl: string,
    shareTitle: string,
    shareDescription: string,

    location: SHARE_LOCATION, //Only used for 'story'/'general' right now
    socialList?: SOCIAL_NETWORK_TYPE[],
    entityType?: 'team' | 'member', 
    viewState?: 'private'
}

const ShareSocials: React.FC<Props> = ({ socialList, shareUrl, shareTitle, shareDescription, location, entityType, viewState, closeModalFn}) => {
    const cms = useContext<any>(CMSContext);
    const modalsCms = cms.modals;

    const [ editingUrl, setEditingUrl ] = useState(false);
    const [ urlPostfix, setUrlPostfix ] = useState(shareUrl.substring(shareUrl.lastIndexOf('/')+1) || '');
    const [ initUrlPostfix ] = useState(urlPostfix);
    const maxUrlPostfixLength = 32;
    const [ char, setChar] = useState(Math.max(0, maxUrlPostfixLength - urlPostfix.length));
    const [ showError, setShowError ] = useState(false);

    const defaultSocialList = [
        {
            type: SOCIAL_NETWORK_TYPE.copyUrl,
            iconClass: "fas fa-clone",
            translation: modalsCms.share.options.url
        },
        {
            type: SOCIAL_NETWORK_TYPE.whatsApp,
            iconClass: "fab fa-whatsapp",
            translation: modalsCms.share.options.whatsapp
        },
        {
            type: SOCIAL_NETWORK_TYPE.facebook,
            iconClass: "fab fa-facebook-f",
            translation: modalsCms.share.options.facebook
        },
        {
            type: SOCIAL_NETWORK_TYPE.twitter,
            iconClass: "fab fa-twitter",
            translation: modalsCms.share.options.twitter
        },
        {
            type: SOCIAL_NETWORK_TYPE.linkedIn,
            iconClass: "fab fa-linkedin-in",
            translation: modalsCms.share.options.linkedin
        },
        {
            type: SOCIAL_NETWORK_TYPE.email,
            iconClass: "fas fa-envelope",
            translation: modalsCms.share.options.email
        },
    ].filter(a=>a);

    let optionList = socialList ? defaultSocialList.filter(i => socialList.includes(i.type)) : defaultSocialList;
    const thisShareModal = (socialNetwork: SOCIAL_NETWORK_TYPE) => {
        switch (socialNetwork) {
            case SOCIAL_NETWORK_TYPE.linkedIn:
                // According to https://developer.linkedin.com/docs/share-on-linkedin the size must be 520 x 570px
                popupCenter('http://www.linkedin.com/shareArticle?mini=true&title='+shareTitle+'&summary='+shareDescription+'&url='+shareUrl, 'linkedin', 520, 570);
                break;
            case SOCIAL_NETWORK_TYPE.twitter:
                // https://dev.twitter.com/web/tweet-button/parameters
                popupCenter('http://twitter.com/share?url='+shareUrl+'&text='+shareDescription, 'twitter', 550, 420);
                break;
            case SOCIAL_NETWORK_TYPE.facebook:
                // https://developers.facebook.com/docs/sharing/reference/share-dialog
                (window as any).FB.ui({
                    method: 'share',
                    display: 'popup',
                    href: shareUrl,
                    quote: shareDescription
                }, function(){});
                break;
            case SOCIAL_NETWORK_TYPE.email:
                window.open('mailto:?subject=' + shareTitle + '&body=' + shareDescription + encodeURIComponent('\r\n' + shareUrl), '_blank');
                break;
            case SOCIAL_NETWORK_TYPE.whatsApp:
                popupCenter('https://api.whatsapp.com/send?text=' + shareDescription + ' ' + shareUrl, 'whatsapp', 550, 420);
                break;
            default:
                return;
        }
    }
    
    function thisCopyToClipBoard() {
        let stringToCopy;
        if (!(entityType === 'team' || entityType === 'member')) {
            stringToCopy = shareUrl;
        } else {
            stringToCopy = `${shareUrl.substring(0, shareUrl.lastIndexOf('/')+1)}${urlPostfix}?mc=1`;
        }
        copyToClipBoard(stringToCopy);
    }

    function getDataAnalyticAttributes(shareType: SOCIAL_NETWORK_TYPE) {
        switch (location){
            case SHARE_LOCATION.mospace:
                return {
                    "data-dl-category": "mo space",
                    "data-dl-location": "hero area",
                    "data-dl-label": "Share Mospace On " + shareType
                };
            case SHARE_LOCATION.events:
                return {
                    "data-dl-category": "events",
                    "data-dl-location": "hero area",
                    "data-dl-label": "Share Event On " + shareType
                };
            case SHARE_LOCATION.donate:
                return {
                    "data-dl-category": "donate",
                    "data-dl-location": "thank you",
                    "data-dl-label": "Share Donation On " + shareType
                };
            case SHARE_LOCATION.story:
                return {
                    "data-dl-category": "content",
                    "data-dl-location": "stories",
                    "data-dl-label": "Share Story On " + shareType
                };
            default:
                return null;
        }
    }

    function onChangeUrl(e: any){
        let value = e.target.value;
        let urlPostfixLength = value.length;
        if(urlPostfixLength <= maxUrlPostfixLength){
            setChar(Math.max(0, maxUrlPostfixLength - urlPostfixLength));
            setUrlPostfix(value);
            setShowError(false);
        }
      
    }

    function onClickSave(){}

    function onClickCancel(){
        setUrlPostfix(initUrlPostfix);
        setChar(Math.max(0, maxUrlPostfixLength - initUrlPostfix.length));
        setEditingUrl(false);
        setShowError(false);
    }

    function getDynamicStyle(which: 'display' | 'edit' | 'icon' | 'error' | 'input'){
        const hide = { style:{display:"none"}},
        error = { style:{border: 'solid 1px #FF0000',  backgroundColor: 'rgba(0, 0, 0, 0.1)'}};
        switch(which){
            case 'display':
                return editingUrl ? hide : null;
            case 'edit':
                return !editingUrl ? hide : null;
            case 'icon':
                return (viewState === 'private' && (entityType === 'team' || entityType === 'member')) ? null : hide;
            case 'error':
                return !showError ? hide : null;
            case 'input':
                return showError ? error : null;
            default:
                return;
        }
    }

    function onClickSocial(type: SOCIAL_NETWORK_TYPE){
        switch (type) {
            case SOCIAL_NETWORK_TYPE.copyUrl:
                thisCopyToClipBoard();
                break;
            case SOCIAL_NETWORK_TYPE.whatsApp:
            case SOCIAL_NETWORK_TYPE.facebook:
            case SOCIAL_NETWORK_TYPE.twitter:
            case SOCIAL_NETWORK_TYPE.linkedIn:
            case SOCIAL_NETWORK_TYPE.email:
                thisShareModal(type);
                break;
            default:
                return;
        }
        closeModalFn && closeModalFn();
    }

    const  popupCenter = (url: string, title: string, w: number, h: number) => {
        // Fixes dual-screen position
        let dualScreenLeft = window.screenLeft,
        dualScreenTop = window.screenTop,
        width = window.innerWidth,
        height = window.innerHeight;

        let left = ((width / 2) - (w / 2)) + dualScreenLeft;
        let top = ((height / 2) - (h / 2)) + dualScreenTop;
        let newWindow = window.open(url, title, 'scrollbars=yes, width=' + w + ', height=' + h + ', top=' + top + ', left=' + left);

        // Puts focus on the newWindow
        if (newWindow && newWindow.focus) {
            newWindow.focus();
        }
    }

    return ( 
        <SocialListContainer>
            <Title>{modalsCms.share.titleStory}</Title>
            <UrlContainer>
                <UrlDisplay {...getDynamicStyle('display')}>
                    <UrlContent>{shareUrl.substring(0, shareUrl.lastIndexOf('/')+1) + urlPostfix}</UrlContent>
                    <EditIcon onClick={() => setEditingUrl(true)}
                        {...getDynamicStyle('icon')}
                        alt-text="Edit Profile URL"
                        data-dl-category="mo space"
                        data-dl-location="hero area"
                        data-dl-label="edit profile url"
                        data-test="share-url-editLink">
                        <img src="/assets/images/icons/pen.png" alt="edit-url"/>
                    </EditIcon>
                </UrlDisplay>
                <UrlEdit {...getDynamicStyle('edit')}>
                    <EditContent>
                        <UrlContent>{shareUrl.substring(0, shareUrl.lastIndexOf('/')+1)}</UrlContent>
                        <EditInput value={urlPostfix} onChange={onChangeUrl} {...getDynamicStyle('input')}/>
                    </EditContent>
                    <Actions>
                        <CharacterLeft>
                        {char}{" "}{"text-mospace-member-title-character-left"}
                        </CharacterLeft>
                        <Error {...getDynamicStyle('error')}
                        dangerouslySetInnerHTML={{__html:"text-error-member-personalurl-notvalid"}}/>
                        <EditButtons>
                            <EditButton isPrimary={true} onClick={onClickSave} {...(initUrlPostfix === urlPostfix) && {disabled:true}}>
                            {"text-mospace-member-title-save"}
                            </EditButton>
                            <EditButton isPrimary={false} onClick={onClickCancel}>
                            {"text-mospace-member-title-cancel"}
                            </EditButton>
                        </EditButtons>
                    </Actions>
                </UrlEdit>
            </UrlContainer>
            { 
                optionList.map((social, index) => (
                    <ButtonContainer key={index}>
                        <Button {...getDataAnalyticAttributes(social.type)}
                                onClick={() => onClickSocial(social.type)}
                                marginTop={(index===0) ? "8px": '0px'}
                                marginBottom={(index===0) ? "16px": '0px'}>
                            <SocialIcon className={social.iconClass}></SocialIcon>
                            {social.translation}
                        </Button>
                    </ButtonContainer>
                ))
            }
        </SocialListContainer>
    );
};
 
export default ShareSocials;
